import { useEffect, useRef } from 'react';
import { runWithKeepalive } from '@/lib/apollo/keepalive';

/** Fires `flush` when the page is being closed or hidden, with keepalive on,
 *  so a debounced editor write is not lost when the tab closes (React unmount
 *  cleanups never run then). `visibilitychange` covers mobile, where a
 *  backgrounded tab may be killed without `pagehide`; flushing early there
 *  only saves sooner. */
export function useFlushOnPageHide(flush: () => void): void {
  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => {
    const fire = () => runWithKeepalive(() => flushRef.current());
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') fire();
    };
    window.addEventListener('pagehide', fire);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', fire);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
